import React, { useState, useEffect } from 'react';
import { workAssignmentAPI, userAPI } from '../services/api';
import styled from 'styled-components';
import { 
  FiBriefcase, 
  FiCalendar, 
  FiAlertTriangle, 
  FiCheckCircle,
  FiInfo,
  FiX,
  FiCheck
} from './Icons';

const DirectAssignContainer = styled.div`
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: bold;
  color: #333;
  font-size: 14px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    border-color: #007bff;
    outline: none;
  }
  
  &:disabled {
    background: #f8f9fa;
    color: #6c757d;
  }
`;

const ShiftTypeContainer = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ShiftButton = styled.button`
  padding: 8px 16px;
  border: 2px solid #007bff;
  border-radius: 4px;
  background: ${props => props.active ? '#007bff' : 'white'};
  color: ${props => props.active ? 'white' : '#007bff'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.active ? '#0056b3' : '#e3f2fd'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;
  
  &.primary {
    background: #930c00;
    color: white;

    &:hover {
      background: #7a0a00;
    }
  }
  
  &.secondary {
    background: #6c757d;
    color: white;
    
    &:hover {
      background: #545b62;
    }
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SelectedDatesDisplay = styled.div`
  grid-column: 1 / -1;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 15px;
  margin-top: 10px;
`;

const DateChip = styled.span`
  display: inline-block;
  background: #007bff;
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  margin: 2px;
`;

const ConflictWarning = styled.div`
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 4px;
  padding: 10px;
  margin: 10px 0;
  color: #856404;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmployeeInfoCard = styled.div`
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  margin-top: 10px;
  font-size: 13px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f1f3f4;
  
  &:last-child {
    border-bottom: none;
  }
`;

const LeaveDate = styled.span`
  background: #fff3cd;
  color: #856404;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin: 1px;
  display: inline-block;
`;

const HolidayDate = styled.span`
  background: #d1ecf1;
  color: #0c5460;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  margin: 1px;
  display: inline-block;
`;

const DirectAssignForm = ({ 
  isActive, 
  onToggle, 
  selectedBranchId,
  currentDate,
  selectedDates,
  onDateSelectionChange,
  onAssignmentSuccess,
  existingAssignments,
  holidays,
  leaveRequests
}) => {
  const [formData, setFormData] = useState({
    role: '',
    employeeId: '',
    shiftType: 'day'
  });
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState(null);

  // Fetch users when component mounts or role changes
  useEffect(() => {
    fetchUsers();
  }, []);

  // Update employee info when employee selection changes
  useEffect(() => {
    if (formData.employeeId) {
      const employee = users.find(u => u.id === parseInt(formData.employeeId));
      if (employee) {
        setSelectedEmployeeInfo(getEmployeeInfo(employee));
      }
    } else {
      setSelectedEmployeeInfo(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.employeeId, users, currentDate, holidays, leaveRequests]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const getEmployeeInfo = (employee) => {
    if (!employee || !currentDate) return null;

    // Get selected month range
    const baseDate = currentDate;
    const monthStart = new Date(baseDate.getFullYear(), baseDate.getMonth(), 1);
    const monthEnd = new Date(baseDate.getFullYear(), baseDate.getMonth() + 1, 0);

    // Filter leaves for this employee in the selected month
    const employeeLeaves = leaveRequests.filter(leave => {
      if (leave.user_id !== employee.id) return false;
      const leaveDate = new Date(leave.leave_date);
      return leaveDate >= monthStart && leaveDate <= monthEnd;
    }).sort((a, b) => new Date(a.leave_date) - new Date(b.leave_date));

    // Filter holidays for the selected month (assuming employee has holiday group)
    const employeeHolidays = holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date || holiday.holiday_date);
      const inRange = holidayDate >= monthStart && holidayDate <= monthEnd;
      if (!inRange) return false;
      // For now include all holidays, can be refined with holiday group logic
      return true;
    }).sort((a, b) => new Date(a.date || a.holiday_date) - new Date(b.date || b.holiday_date));

    return {
      employee,
      leaves: employeeLeaves,
      holidays: employeeHolidays,
      timeframe: { start: monthStart, end: monthEnd }
    };
  };

  // Map simplified role categories to actual roles (base + supervisor)
  const getRolesForCategory = (category) => {
    const roleMap = {
      'manager': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[category] || [category];
  };

  const getFilteredUsers = () => {
    if (!formData.role) return [];
    
    const allowedRoles = getRolesForCategory(formData.role);
    
    const filtered = users.filter(user => {
      const userRole = String(user.role || '').toLowerCase().trim();
      return allowedRoles.includes(userRole);
    });

    // Sort by priority: home branch > moveable > non-moveable
    const selectedBranchIdNum = parseInt(selectedBranchId);
    
    return filtered.sort((a, b) => {
      const aHomeBranchId = a.home_branch_id || a.home_branch?.id;
      const bHomeBranchId = b.home_branch_id || b.home_branch?.id;
      
      const aIsFromSelectedBranch = selectedBranchIdNum && aHomeBranchId === selectedBranchIdNum;
      const bIsFromSelectedBranch = selectedBranchIdNum && bHomeBranchId === selectedBranchIdNum;
      
      if (aIsFromSelectedBranch && !bIsFromSelectedBranch) return -1;
      if (!aIsFromSelectedBranch && bIsFromSelectedBranch) return 1;
      
      if (aIsFromSelectedBranch === bIsFromSelectedBranch) {
        if (a.is_moveable && !b.is_moveable) return -1;
        if (!a.is_moveable && b.is_moveable) return 1;
        
        const aName = `${a.first_name} ${a.last_name}`.toLowerCase();
        const bName = `${b.first_name} ${b.last_name}`.toLowerCase();
        return aName.localeCompare(bName);
      }
      
      return 0;
    });
  };

  const getConflictsForDates = () => {
    if (!formData.employeeId || selectedDates.length === 0) return { conflicts: [], warnings: [] };

    const employee = users.find(u => u.id === parseInt(formData.employeeId));
    if (!employee) return { conflicts: [], warnings: [] };

    const conflicts = [];
    const warnings = [];

    selectedDates.forEach(date => {
      let dateStr;
      if (typeof date === 'string') {
        dateStr = date;
      } else {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateStr = `${year}-${month}-${day}`;
      }
      
      // Check for leave conflicts
      const leaveConflict = leaveRequests.find(leave => 
        leave.user_id === employee.id && leave.leave_date === dateStr
      );
      
      if (leaveConflict) {
        conflicts.push({
          date: dateStr,
          type: 'leave',
          description: `On ${leaveConflict.leave_type?.name || 'leave'}`
        });
      }
      
      // Check for existing assignment conflicts
      const assignmentConflict = existingAssignments.find(assignment => 
        assignment.user_id === employee.id && 
        assignment.assignment_date === dateStr && 
        assignment.shift_type === formData.shiftType
      );
      
      if (assignmentConflict) {
        conflicts.push({
          date: dateStr,
          type: 'assignment',
          description: `Already assigned (${formData.shiftType} shift)`
        });
      }
      
      // Check for holiday warnings
      const holidayConflict = holidays.find(holiday => 
        (holiday.date === dateStr || holiday.holiday_date === dateStr) && 
        holiday.holiday_group_id === employee.holiday_group_id
      );
      
      if (holidayConflict) {
        warnings.push({
          date: dateStr,
          type: 'holiday',
          description: `Holiday: ${holidayConflict.name} (Overtime)`
        });
      }
    });

    return { conflicts, warnings };
  };

  const getConsecutiveDaysWarning = () => {
    if (!formData.employeeId || selectedDates.length === 0) return null;

    const sortedDates = [...selectedDates]
      .map(date => {
        if (typeof date === 'string') {
          return date;
        } else {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        }
      })
      .sort();

    // Check for consecutive dates
    let maxConsecutive = 1;
    let currentConsecutive = 1;
    
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = new Date(sortedDates[i-1]);
      const currentDate = new Date(sortedDates[i]);
      const daysDiff = (currentDate - prevDate) / (1000 * 60 * 60 * 24);
      
      if (daysDiff === 1) {
        currentConsecutive++;
        maxConsecutive = Math.max(maxConsecutive, currentConsecutive);
      } else {
        currentConsecutive = 1;
      }
    }
    
    return maxConsecutive > 6 ? maxConsecutive : null;
  };

  const handleRoleChange = (role) => {
    setFormData(prev => ({
      ...prev,
      role,
      employeeId: '' // Reset employee selection when role changes
    }));
    setError('');
  };

  const handleEmployeeChange = (employeeId) => {
    setFormData(prev => ({
      ...prev,
      employeeId
    }));
    setError('');
  };

  const handleShiftTypeChange = (shiftType) => {
    setFormData(prev => ({
      ...prev,
      shiftType
    }));
  };

  const clearSelection = () => {
    onDateSelectionChange([]);
    setError('');
  };

  const handleAssign = async () => {
    // Validation
    if (!formData.role) {
      setError('Please select a role');
      return;
    }
    if (!formData.employeeId) {
      setError('Please select an employee');
      return;
    }
    if (selectedDates.length === 0) {
      setError('Please select at least one date');
      return;
    }

    const { conflicts } = getConflictsForDates();
    if (conflicts.length > 0) {
      setError(`Cannot assign: ${conflicts[0].description} on ${conflicts[0].date}`);
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Get the actual user role (not the category)
      const selectedUser = users.find(u => u.id === parseInt(formData.employeeId));
      if (!selectedUser) {
        setError('Selected employee not found');
        setLoading(false);
        return;
      }
      
      const assignments = selectedDates.map(date => {
        let dateStr;
        if (typeof date === 'string') {
          dateStr = date;
        } else {
          // Use manual date formatting to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          dateStr = `${year}-${month}-${day}`;
        }
        
        return {
          user_id: parseInt(formData.employeeId),
          branch_id: parseInt(selectedBranchId),
          assignment_date: dateStr,
          shift_type: formData.shiftType,
          role: selectedUser.role  // Use actual user role, not category
        };
      });

      const response = await workAssignmentAPI.createBulk(assignments);

      // Reset form and selection
      setFormData({ role: '', employeeId: '', shiftType: 'day' });
      clearSelection();
      
      // Handle success callback separately to avoid showing error if callback fails
      if (onAssignmentSuccess) {
        try {
          await onAssignmentSuccess();
        } catch (callbackError) {
          console.error('Error in success callback:', callbackError);
          // Don't show error to user - assignments were created successfully
        }
      }
      
    } catch (error) {
      console.error('Error creating assignments:', error);
      console.error('Error response:', error.response?.data);
      
      let errorMessage = 'Error creating assignments';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // Handle structured error format
        if (typeof detail === 'object' && detail.message && detail.errors) {
          errorMessage = `${detail.message}\n\nDetails:\n${detail.errors.join('\n')}`;
        }
        // Handle legacy object with errors array only
        else if (typeof detail === 'object' && detail.errors && Array.isArray(detail.errors)) {
          errorMessage = `Error creating assignments:\n${detail.errors.join('\n')}`;
        } 
        // Handle simple string detail
        else if (typeof detail === 'string') {
          errorMessage = `Error creating assignments: ${detail}`;
        } 
        // Handle unknown object format
        else {
          errorMessage = `Error creating assignments: ${JSON.stringify(detail)}`;
        }
      } else if (error.message) {
        errorMessage = `Error creating assignments: ${error.message}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isActive) {
    return (
      <DirectAssignContainer>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h4 style={{ margin: 0, color: '#495057' }}>
              <FiBriefcase style={{ marginRight: '8px' }} />
              Direct Assignment Mode
            </h4>
            <p style={{ margin: '4px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
              Quickly assign individual employees to multiple dates
            </p>
          </div>
          <ActionButton className="primary" onClick={onToggle}>
            Enable Direct Assign
          </ActionButton>
        </div>
      </DirectAssignContainer>
    );
  }

  const filteredUsers = getFilteredUsers();
  const { conflicts, warnings } = getConflictsForDates();
  const consecutiveWarning = getConsecutiveDaysWarning();

  return (
    <DirectAssignContainer>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h4 style={{ margin: 0, color: '#495057' }}>
          <FiBriefcase style={{ marginRight: '8px' }} />
          Direct Assignment Mode - Active
        </h4>
        <ActionButton className="secondary" onClick={onToggle}>
          <FiX /> Exit Direct Assign
        </ActionButton>
      </div>

      <FormRow>
        {/* Role Selection */}
        <FormSection>
          <Label>Role *</Label>
          <Select 
            value={formData.role} 
            onChange={(e) => handleRoleChange(e.target.value)}
          >
            <option value="">Select role...</option>
            <option value="manager">Manager</option>
            <option value="makeup">Makeup</option>
            <option value="photographer">Photographer</option>
            <option value="retoucher">Retoucher</option>
          </Select>
        </FormSection>

        {/* Employee Selection */}
        <FormSection>
          <Label>
            Employee {formData.role && `(${formData.role})`}
            {filteredUsers.length > 0 && ` - ${filteredUsers.length} available`}
          </Label>
          <Select 
            value={formData.employeeId} 
            onChange={(e) => handleEmployeeChange(e.target.value)}
            disabled={!formData.role}
          >
            <option value="">Select employee...</option>
            {filteredUsers.map(user => {
              const homeBranchName = user.home_branch?.name || 'Unknown';
              const moveableText = user.is_moveable ? 'Moveable' : 'Not Moveable';
              
              return (
                <option key={user.id} value={user.id}>
                  {user.name} ({homeBranchName} - {moveableText})
                </option>
              );
            })}
          </Select>
        </FormSection>

        {/* Shift Type Selection */}
        <FormSection>
          <Label>Shift Type</Label>
          <ShiftTypeContainer>
            <ShiftButton
              type="button"
              active={formData.shiftType === 'day'}
              onClick={() => handleShiftTypeChange('day')}
            >
              Day Shift
            </ShiftButton>
            <ShiftButton
              type="button"
              active={formData.shiftType === 'noon'}
              onClick={() => handleShiftTypeChange('noon')}
            >
              Noon Shift
            </ShiftButton>
          </ShiftTypeContainer>
        </FormSection>
      </FormRow>

      {/* Selected Dates Display */}
      {selectedDates.length > 0 && (
        <SelectedDatesDisplay>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: 'bold', color: '#495057' }}>
              <FiCalendar style={{ marginRight: '6px' }} />
              Selected Dates ({selectedDates.length})
            </span>
            <ActionButton className="danger" onClick={clearSelection} style={{ padding: '4px 8px', fontSize: '12px' }}>
              <FiX /> Clear All
            </ActionButton>
          </div>
          <div>
            {selectedDates.map(date => {
              let dateStr;
              if (typeof date === 'string') {
                dateStr = date;
              } else {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                dateStr = `${year}-${month}-${day}`;
              }
              return (
                <DateChip key={dateStr}>
                  {new Date(dateStr).toLocaleDateString()}
                </DateChip>
              );
            })}
          </div>
        </SelectedDatesDisplay>
      )}

      {/* Conflict Warnings */}
      {conflicts.length > 0 && (
        <ConflictWarning>
          <FiAlertTriangle />
          <div>
            <strong>Conflicts Detected:</strong>
            <div style={{ marginTop: '4px' }}>
              {conflicts.slice(0, 3).map(conflict => (
                <div key={`${conflict.date}-${conflict.type}`} style={{ fontSize: '12px' }}>
                  {new Date(conflict.date).toLocaleDateString()}: {conflict.description}
                </div>
              ))}
              {conflicts.length > 3 && <div style={{ fontSize: '12px' }}>...and {conflicts.length - 3} more</div>}
            </div>
          </div>
        </ConflictWarning>
      )}

      {/* Holiday Warnings */}
      {warnings.length > 0 && (
        <ConflictWarning style={{ background: '#fff3cd', borderColor: '#ffeaa7' }}>
          <FiInfo />
          <div>
            <strong>Holiday Overtime:</strong>
            <div style={{ marginTop: '4px' }}>
              {warnings.slice(0, 2).map(warning => (
                <div key={`${warning.date}-${warning.type}`} style={{ fontSize: '12px' }}>
                  {new Date(warning.date).toLocaleDateString()}: {warning.description}
                </div>
              ))}
              {warnings.length > 2 && <div style={{ fontSize: '12px' }}>...and {warnings.length - 2} more</div>}
            </div>
          </div>
        </ConflictWarning>
      )}

      {/* Consecutive Days Warning */}
      {consecutiveWarning && (
        <ConflictWarning style={{ background: '#e7f3ff', borderColor: '#b3d9ff', color: '#0066cc' }}>
          <FiInfo />
          <div>
            <strong>Notice:</strong> {consecutiveWarning} consecutive work days detected. Consider employee workload.
          </div>
        </ConflictWarning>
      )}

      {/* Employee Information */}
      {selectedEmployeeInfo && (
        <EmployeeInfoCard>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#495057' }}>
            {selectedEmployeeInfo.employee.name} - {currentDate?.toLocaleDateString('en', { month: 'long', year: 'numeric' })}
          </div>
          
          <InfoRow>
            <span>Leave Requests:</span>
            <div>
              {selectedEmployeeInfo.leaves.length === 0 ? (
                <span style={{ color: '#2e7d5b' }}><FiCheckCircle style={{ marginRight: '4px' }} />None</span>
              ) : (
                selectedEmployeeInfo.leaves.map(leave => (
                  <LeaveDate key={leave.id}>
                    {new Date(leave.leave_date).toLocaleDateString()}
                  </LeaveDate>
                ))
              )}
            </div>
          </InfoRow>
          
          <InfoRow>
            <span>Holidays:</span>
            <div>
              {selectedEmployeeInfo.holidays.length === 0 ? (
                <span style={{ color: '#6c757d' }}>None</span>
              ) : (
                selectedEmployeeInfo.holidays.map(holiday => (
                  <HolidayDate key={holiday.id}>
                    {new Date(holiday.date || holiday.holiday_date).toLocaleDateString()} - {holiday.name}
                  </HolidayDate>
                ))
              )}
            </div>
          </InfoRow>
        </EmployeeInfoCard>
      )}

      {/* Action Buttons */}
      <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontSize: '14px', color: '#6c757d', fontStyle: 'italic' }}>
          <FiInfo style={{ marginRight: '4px' }} />
          Click calendar dates to select, then click Assign to create work assignments
        </div>
        
        <ActionButtons>
          <ActionButton 
            className="primary"
            onClick={handleAssign}
            disabled={loading || !formData.role || !formData.employeeId || selectedDates.length === 0 || conflicts.length > 0}
          >
            <FiCheck />
            {loading ? 'Assigning...' : `Assign ${selectedDates.length} Date${selectedDates.length !== 1 ? 's' : ''}`}
          </ActionButton>
        </ActionButtons>
      </div>

      {error && (
        <div style={{ 
          marginTop: '10px', 
          color: '#dc3545', 
          fontSize: '14px',
          background: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          padding: '8px 12px'
        }}>
          {error}
        </div>
      )}
    </DirectAssignContainer>
  );
};

export default DirectAssignForm;